import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TFSLoader } from '../components/ui';
import LogisticsLogo from '../assets/logistics_logo.svg';
import { EyeIcon, EyeSlashIcon, UserIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, initializing, isAuthenticated } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    console.log('Login attempt started...');
    const result = await login(username, password, rememberMe);
    console.log('Login result:', result);
    
    setSubmitting(false);
    
    if (result.success) {
      console.log('Login successful, redirecting to:', from);
      // Small delay to ensure state updates are processed
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
    } else {
      console.log('Login failed:', result.error);
    }
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center h-screen">
        <TFSLoader />
      </div>
    );
  }

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/20 bg-white/95 backdrop-blur-sm shadow-xl overflow-hidden">
        <div className="px-6 py-6">
          <div className="flex items-center justify-center mb-4">
            <img src={LogisticsLogo} alt="TFS Logo" className="h-10 w-auto" />
          </div>
          <h1 className="text-xl font-semibold text-center text-gray-900">TFS Login</h1>
          <p className="text-sm text-gray-600 text-center mt-1 mb-6">Use your credentials to access the dashboard</p>
    
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-800">Username</label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-gray-300 bg-white/90 text-gray-900 pl-10 pr-3 py-2.5 shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 hover:border-gray-400 hover:bg-white"
                  placeholder="Enter username"
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>
    
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-800">Password</label>
                <button
                  type="button"
                  className="text-xs text-indigo-600 hover:text-indigo-700 transition-colors"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <span className="inline-flex items-center gap-1"><EyeSlashIcon className="h-4 w-4" /> Hide</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><EyeIcon className="h-4 w-4" /> Show</span>
                  )}
                </button>
              </div>
              <div className="relative">
                <LockClosedIcon className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-gray-300 bg-white/90 text-gray-900 pl-10 pr-10 py-2.5 shadow-sm transition-all focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 hover:border-gray-400 hover:bg-white"
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>
    
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Remember me</span>
              </label>
            </div>
    
            <button
              type="submit"
              disabled={submitting}
              className={`mt-2 w-full rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-all ${
                submitting 
                  ? 'bg-indigo-400 cursor-not-allowed' 
                  : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-xl active:scale-[0.98]'
              }`}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
    
          <div className="mt-6 text-[11px] text-gray-600 text-center">
            By signing in, you agree to secure use of TFS Portal.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;